import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { PppcChoice, PppcServiceDef } from "@/types/pppc";
import { Info, Trash2, Wrench } from "lucide-react";

interface PrivacyServiceRowProps {
  service: PppcServiceDef;
  /** Always "allow" or "deny" — absence is represented by removing the row entirely. */
  value: "allow" | "deny";
  onChange: (next: "allow" | "deny") => void;
  onRemove: () => void;
  disabled?: boolean;
  isCustom?: boolean;
}

/**
 * A configured PPPC service row. Renders the service label/description on the
 * left and a segmented Allow/Deny control + remove button on the right. Deny-
 * only services lock the toggle to "Deny" with a tooltip explanation.
 */
export const PrivacyServiceRow = ({
  service,
  value,
  onChange,
  onRemove,
  disabled,
  isCustom,
}: PrivacyServiceRowProps) => {
  const denyOnly = !!service.denyOnly;

  const SegBtn = ({
    choice,
    children,
  }: {
    choice: "allow" | "deny";
    children: React.ReactNode;
  }) => {
    const active = value === choice;
    const isDisabled = disabled || (denyOnly && choice === "allow");
    const activeClass =
      choice === "allow"
        ? "bg-success text-success-foreground shadow-sm"
        : "bg-destructive text-destructive-foreground shadow-sm";
    return (
      <button
        type="button"
        disabled={isDisabled}
        onClick={() => !isDisabled && onChange(choice)}
        aria-pressed={active}
        className={cn(
          "px-3 h-7 text-xs font-medium transition-colors",
          active
            ? activeClass
            : "text-muted-foreground hover:text-foreground hover:bg-muted",
          isDisabled && !active && "opacity-50 cursor-not-allowed",
        )}
      >
        {children}
      </button>
    );
  };

  return (
    <div className="flex items-start justify-between gap-4 py-3 px-4 border-b last:border-b-0">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium">
            {isCustom ? service.key : service.label}
          </span>
          {isCustom && (
            <TooltipProvider delayDuration={150}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Badge
                    variant="outline"
                    className="text-[10px] gap-1 cursor-help border-warning/60 text-warning bg-warning/10"
                  >
                    <Wrench className="h-3 w-3" />
                    Custom
                  </Badge>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-xs">
                  This TCC service isn't in our built-in catalog. It will be sent
                  to macOS exactly as written. Double-check the key matches an
                  identifier Apple ships, otherwise the policy will be silently
                  ignored on the device.
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          {denyOnly && (
            <TooltipProvider delayDuration={150}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Badge variant="outline" className="text-[10px] gap-1 cursor-help">
                    <Info className="h-3 w-3" />
                    Deny only
                  </Badge>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-xs">
                  Apple restricts MDM from granting this service. You can only
                  pre-deny it; the user must grant it themselves.
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
        <p className="text-xs text-muted-foreground mt-1">{service.description}</p>
        {isCustom && (
          <p className="text-[11px] text-muted-foreground mt-1 flex items-center gap-1">
            <Info className="h-3 w-3 shrink-0" />
            <span>
              Sent to macOS as{" "}
              <code className="font-mono px-1 py-0.5 rounded bg-muted">
                Services.{service.key}
              </code>
              . Verify against{" "}
              <a
                href="https://developer.apple.com/documentation/devicemanagement/tcc"
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-foreground"
              >
                Apple's TCC reference
              </a>
              .
            </span>
          </p>
        )}
      </div>

      <div className="flex items-center gap-2 shrink-0">
        <div
          role="group"
          aria-label={`${service.label} permission`}
          className="inline-flex rounded-md border overflow-hidden"
        >
          <SegBtn choice="allow">Allow</SegBtn>
          <span className="w-px bg-border" aria-hidden="true" />
          <SegBtn choice="deny">Deny</SegBtn>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-7 w-7 text-muted-foreground hover:text-destructive"
          onClick={onRemove}
          disabled={disabled}
          aria-label={`Remove ${service.label}`}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
};
